import React, { createContext, useContext, useState, useRef } from 'react';
import { API_BASE_URL } from '../config';

// Create the context
const FarmDataContext = createContext();

// Provider component
export const FarmDataProvider = ({ children }) => {
    const [farmData, setFarmData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchingRef = useRef(false);
    const lastFetchRef = useRef({ farm_id: null, timestamp: null });

    // Function to fetch farm data
    const fetchFarmData = async (farm_id, idToken) => {
        // Prevent multiple concurrent fetches
        if (fetchingRef.current) {
            console.log('FarmDataContext: Fetch already in progress, skipping...');
            return;
        }

        // Check if we already have data for this farm_id and it's recent (within 5 minutes)
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        if (farmData && lastFetchRef.current.farm_id === farm_id && 
            (now - lastFetchRef.current.timestamp) < fiveMinutes) {
            console.log('FarmDataContext: Using cached data for farm_id:', farm_id);
            return;
        }

        console.log('FarmDataContext: Fetching fresh data for farm_id:', farm_id);
        fetchingRef.current = true;
        setLoading(true);
        setError(null);
        
        try {
            const endpoints = [
                `/farm/${farm_id}`,
                `/farm/${farm_id}/crops`,
                `/farm/${farm_id}/fertilizer_events`,
                `/farm/${farm_id}/fungicide_events`,
                `/farm/${farm_id}/insecticide_events`,
                `/farm/${farm_id}/herbicide_events`,
                `/farm/${farm_id}/harvests`,
                `/farm/${farm_id}/sales`,
                `/farm/${farm_id}/inputs`
            ];

            const requests = endpoints.map((endpoint) =>
                fetch(`${API_BASE_URL}${endpoint}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${idToken}`,
                    },
                }).then((res) => res.json())
            );

            const results = await Promise.all(requests);
            const [
                farmDetails,
                crops,
                fertilizerEvents,
                fungicideEvents,
                insecticideEvents,
                herbicideEvents,
                harvests,
                sales,
                inputs
            ] = results;

            const newFarmData = {
                farmDetails,
                crops,
                fertilizerEvents,
                fungicideEvents,
                insecticideEvents,
                herbicideEvents,
                harvests,
                sales,
                inputs,
                zones: farmDetails.zones || []
            };

            console.log('FarmDataContext: Data fetched successfully:', {
                farm_id,
                cropsLength: crops?.length,
                harvestsLength: harvests?.length,
                zonesLength: farmDetails.zones?.length,
                fertilizerEventsLength: fertilizerEvents?.length
            });

            setFarmData(newFarmData);
            lastFetchRef.current = { farm_id, timestamp: now };
        } catch (err) {
            console.error('Error fetching farm data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    };

    return (
        <FarmDataContext.Provider value={{ farmData, fetchFarmData, loading, error }}>
            {children}
        </FarmDataContext.Provider>
    );
};

// Custom hook to use the FarmDataContext
export const useFarmData = () => {
    const context = useContext(FarmDataContext);
    if (!context) {
        throw new Error('useFarmData must be used within a FarmDataProvider');
    }
    return context;
};